<template>
  <main id="ordini-page" class="page-container" v-if="$route.name === 'ordini'">
    <div class="table-container-ordini">
      <!-- Toolbar -->
      <div class="ordini-toolbar">
        <div class="ordini-tabs">
          <button
            :class="['tab-btn', { active: !showArchived }]"
            @click="switchTab(false)"
          >
            In corso
          </button>
          <button
            :class="['tab-btn', { active: showArchived }]"
            @click="switchTab(true)"
          >
            Completati
          </button>
        </div>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing"
          title="Aggiorna"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>

      <!-- Tabella -->
      <div class="tbl-wrap" :class="{ dimmed: isLoading }">
        <table class="tbl quote-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Da saldare</th>
              <th>Data</th>
              <th>Consegna / Ritiro</th>
              <th>Pagamento</th>
              <th>Spedizione</th>
              <th class="doc-cell">Documenti</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="quote in allQuotes" :key="quote.id" class="quote-row">
              <!-- Cliente con tooltip contatti -->
              <td
                class="client-cell"
                @mouseenter="onCellEnter(quote.id)"
                @mouseleave="onCellLeave(quote.id)"
              >
                <button
                  class="client-trigger"
                  @focus="onCellEnter(quote.id)"
                  @blur="onCellLeave(quote.id)"
                  @click.stop.prevent="toggleTooltip(quote.id)"
                  @touchstart.stop.prevent="onTriggerTouch(quote.id)"
                  @keydown="onTriggerKeydown($event, quote.id)"
                  :aria-expanded="isTooltipVisible(quote.id)"
                  :aria-describedby="'tt-' + quote.id"
                  type="button"
                >
                  {{ quote.Client?.clientName || "—" }}
                  <span class="sr-only">Apri contatti</span>
                </button>
                <transition name="fade">
                  <div
                    v-if="isTooltipVisible(quote.id)"
                    :id="'tt-' + quote.id"
                    role="tooltip"
                    class="email-tooltip"
                    :class="{ 'no-pointer': lockClickId === quote.id }"
                    @click.stop
                    @touchstart.stop
                  >
                    <div class="tooltip-row" v-if="quote?.Client?.email">
                      <span class="label">Email</span>
                      <a :href="'mailto:' + quote.Client.email">{{
                        quote.Client.email
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.email, 'email')"
                        title="Copia email"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>
                    <div class="tooltip-row" v-if="quote?.Client?.pec">
                      <span class="label">PEC</span>
                      <a :href="'mailto:' + quote.Client.pec">{{
                        quote.Client.pec
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.pec, 'pec')"
                        title="Copia PEC"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>
                    <div class="tooltip-row" v-if="quote?.Client?.phoneNumber">
                      <span class="label">Telefono</span>
                      {{ quote.Client.phoneNumber }}
                      <button
                        class="copy-btn"
                        @click="
                          copyToClipboard(quote.Client.phoneNumber, 'phone')
                        "
                        title="Copia numero"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>
                    <div
                      class="tooltip-row muted"
                      v-if="
                        !quote?.Client?.email &&
                        !quote?.Client?.pec &&
                        !quote?.Client?.phoneNumber
                      "
                    >
                      Nessun contatto disponibile.
                    </div>
                  </div>
                </transition>
              </td>

              <td>{{ formatPriceToPrint(quote.realTotalPrice) }}</td>
              <td class="date-cell">{{ formatToLocalDate(quote.date) }}</td>
              <td class="address-cell">
                <span
                  v-if="quote.deliveryType === 'ritiro'"
                  class="badge badge--ritiro"
                  >Ritiro</span
                >
                <a
                  v-else-if="quote.location && quote.location.link"
                  :href="quote.location.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="custom-link"
                  >{{ formatAddress(quote.location.address) }}</a
                >
                <span v-else>{{
                  formatAddress(quote.location?.address || "—")
                }}</span>
              </td>

              <!-- Pagamento -->
              <td class="state-container">
                <StatusSelect
                  :modelValue="quote.payment_state"
                  :options="PAGAMENTO_OPTIONS"
                  :disabled="quote.payment_state === 'Pagato'"
                  @update:modelValue="
                    (val) => {
                      quote.payment_state = val;
                      onQuoteStateChange(quote, 'payment_state', val);
                    }
                  "
                />
              </td>

              <!-- Spedizione -->
              <td class="state-container">
                <StatusSelect
                  :modelValue="quote.state"
                  :options="spedizioneOptions(quote)"
                  :disabled="isStateSelectDisabled(quote)"
                  @update:modelValue="
                    (val) => {
                      quote.state = val;
                      onQuoteStateChange(quote, 'state', val);
                    }
                  "
                />
              </td>

              <!-- Documenti -->
              <td class="doc-cell">
                <button
                  class="doc-btn doc-btn--pdf"
                  type="button"
                  title="Preventivo PDF"
                  @click="openDocumentsModal(quote, 'pdf')"
                >
                  <i class="fa-solid fa-file-pdf"></i>
                  <span>PDF</span>
                </button>
                <button
                  class="doc-btn doc-btn--ddt"
                  type="button"
                  title="Storico DDT"
                  @click="openDocumentsModal(quote, 'ddt')"
                >
                  <i class="fa-solid fa-truck"></i>
                  <span>DDT</span>
                </button>
              </td>

              <!-- Azioni -->
              <td class="actions-cell">
                <router-link
                  :to="`/home/ordini/dettaglio/${quote.id}`"
                  class="icons-button"
                  title="Visualizza dettaglio ordine"
                  aria-label="Visualizza dettaglio ordine"
                >
                  <i class="fa-solid fa-eye"></i>
                </router-link>
                <button
                  v-if="!showArchived"
                  class="icons-button"
                  type="button"
                  title="Gestisci pagamenti"
                  @click="openPaymentModal(quote)"
                >
                  <i class="fa-solid fa-euro"></i>
                </button>
                <button
                  v-if="!showArchived"
                  class="icons-button"
                  type="button"
                  title="Genera DDT"
                  @click="openDdtModal(quote)"
                >
                  <i class="fa-solid fa-truck"></i>
                </button>
                <button
                  class="icons-button"
                  type="button"
                  title="Storico consegne"
                  @click="openHistoryModal(quote)"
                >
                  <i class="fa-solid fa-clock-rotate-left"></i>
                </button>
                <button
                  v-if="!showArchived"
                  class="icons-button"
                  type="button"
                  title="Reimposta a preventivo"
                  @click="openResetModal(quote)"
                >
                  <i class="fa-solid fa-rotate-left"></i>
                </button>
                <button
                  v-if="!showArchived"
                  class="icons-button icons-button--danger"
                  @click="openArchiveModal(quote)"
                  title="Archivia ordine"
                  type="button"
                >
                  <i class="fa-solid fa-trash-can"></i>
                </button>
                <button
                  v-else-if="canRestore(quote)"
                  class="icons-button icons-button--danger"
                  @click="openRestoreModal(quote)"
                  title="Ripristina ordine"
                  type="button"
                >
                  <i class="fa-solid fa-trash-arrow-up"></i>
                </button>
              </td>
            </tr>
            <tr v-if="allQuotes.length === 0 && !isLoading">
              <td colspan="8" class="no-result">Nessun ordine trovato.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginazione / ricerca -->
      <div class="pagination">
        <div class="pagination-left">
          <div class="search-container">
            <i
              v-if="!searchTerm"
              class="fa-solid fa-magnifying-glass search-icon"
            ></i>
            <input
              type="text"
              v-model="searchTerm"
              @keyup.enter="onSearch"
              placeholder="Cerca ordini..."
              class="search-box"
            />
            <button
              v-if="searchTerm"
              @click="onResetSearch"
              class="clear-search-button"
              :disabled="isLoading"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>
        <div class="pagination-right">
          <div class="pagination-info">
            {{ pagination.startItem }}-{{ pagination.endItem }} di
            {{ pagination.totalItems }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="isLoading || pagination.currentPage === 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="isLoading || pagination.currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- MODALE DOCUMENTI -->
      <VueFinalModal
        v-model="showDocumentsModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeDocumentsModal"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">
            {{ docModalSection === "ddt" ? "Storico DDT" : "Documenti ordine" }}
            #{{ currentQuoteId }}
          </h3>

          <template v-if="docModalSection === 'pdf'">
            <transition-group name="card" tag="div" class="documents-grid">
              <button
                key="regen-action"
                type="button"
                class="document-card document-card--action"
                :disabled="regenPdfBusy || !currentQuoteId"
                title="Rigenera il PDF ordine aggiornato: lo scarica e aggiorna il documento in elenco"
                @click="regenerateOrderPdf"
              >
                <span class="card-icon-pdf">
                  <i
                    class="fa-solid"
                    :class="
                      regenPdfBusy ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'
                    "
                    aria-hidden="true"
                  ></i>
                </span>
                <span class="card-body">
                  <span class="card-title">{{
                    regenPdfBusy ? "Rigenerazione…" : "Rigenera PDF"
                  }}</span>
                  <span class="card-subtitle"
                    >Aggiorna il documento e scaricalo</span
                  >
                </span>
              </button>
              <div
                v-for="(doc, i) in filteredDocuments"
                :key="safeDocKey(doc, i)"
                class="document-card"
              >
                <a :href="doc.url" target="_blank" class="card-link">
                  <div class="card-icon-pdf">
                    <i class="fa-solid fa-file-pdf"></i>
                  </div>
                  <div class="card-body">
                    <div class="card-title">{{ formatDocName(doc.name) }}</div>
                    <div class="card-subtitle">
                      Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                    </div>
                  </div>
                </a>
                <button
                  class="icons-button card-email-btn"
                  :class="{ 'email-sent': emailSentMap[selectedQuote?.id] }"
                  :title="
                    emailSentMap[selectedQuote?.id]
                      ? 'Email già inviata in questa sessione'
                      : 'Invia email al cliente'
                  "
                  @click.stop="onSendQuoteEmail(selectedQuote)"
                >
                  <i class="fa-solid fa-envelope"></i>
                </button>
              </div>
              <div v-if="filteredDocuments.length === 0" class="no-result">
                Nessun documento trovato
              </div>
            </transition-group>
          </template>

          <template v-if="docModalSection === 'ddt'">
            <div class="ddt-history">
              <div v-if="selectedDdtDocuments.length === 0" class="no-result">
                Nessun DDT trovato per questo ordine.
              </div>
              <template v-if="ddtDocumentsByCausale.consegna.length > 0">
                <div class="ddt-history__group-label">
                  Consegne
                  <span class="ddt-history__group-count"
                    >({{ ddtDocumentsByCausale.consegna.length }})</span
                  >
                </div>
                <div
                  v-for="(doc, idx) in ddtDocumentsByCausale.consegna"
                  :key="doc.id"
                  class="ddt-history__row"
                >
                  <a :href="doc.url" target="_blank" class="ddt-history__link">
                    <i class="fa-solid fa-file-arrow-down"></i>
                    DDT N° {{ doc._ddtId }}
                    <span class="ddt-history__seq">Consegna {{ idx + 1 }}</span>
                  </a>
                  <span class="ddt-history__date">{{
                    formatToLocalDate(doc.createdAt)
                  }}</span>
                </div>
              </template>
              <template v-if="ddtDocumentsByCausale.ritiro.length > 0">
                <div class="ddt-history__group-label">Ritiri</div>
                <div
                  v-for="doc in ddtDocumentsByCausale.ritiro"
                  :key="doc.id"
                  class="ddt-history__row"
                >
                  <a :href="doc.url" target="_blank" class="ddt-history__link">
                    <i class="fa-solid fa-file-arrow-down"></i>
                    DDT N° {{ doc._ddtId }}
                  </a>
                  <span class="ddt-history__date">{{
                    formatToLocalDate(doc.createdAt)
                  }}</span>
                </div>
              </template>
            </div>
          </template>
        </template>
      </VueFinalModal>

      <!-- MODALE CONFERMA CAMBIO STATO -->
      <VueFinalModal
        v-model="showConfirmModal"
        :clickToClose="!confirmBusy"
        :escToClose="!confirmBusy"
        classes="documents-modal"
        content-class="confirm-modal-content modal--state"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="handleConfirmCancel"
            :disabled="confirmBusy"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">{{ confirmTitle }}</h3>
          <p class="modal__subtitle" v-html="confirmMessage"></p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="handleConfirmCancel"
              :disabled="confirmBusy"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="handleConfirmOK"
              :disabled="confirmBusy"
            >
              <span v-if="confirmBusy">{{ confirmBusyLabel }}</span>
              <span v-else>{{ confirmLabel }}</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE ARCHIVIAZIONE -->
      <VueFinalModal
        v-model="showArchiveModal"
        :clickToClose="!archiving"
        :escToClose="!archiving"
        classes="documents-modal"
        content-class="confirm-modal-content modal--archive"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeArchiveModal"
            :disabled="archiving"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Archivia ordine</h3>
          <p class="modal__subtitle">
            Archiviare l'ordine
            <strong>#{{ currentQuoteId }}</strong> di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong
            >?
          </p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeArchiveModal"
              :disabled="archiving"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmArchive"
              :disabled="archiving"
            >
              <span v-if="archiving">Archivia…</span>
              <span v-else>Archivia</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE RIPRISTINO -->
      <VueFinalModal
        v-model="showRestoreModal"
        :clickToClose="!restoring"
        :escToClose="!restoring"
        classes="documents-modal"
        content-class="confirm-modal-content modal--restore"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeRestoreModal"
            :disabled="restoring"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Ripristina ordine</h3>
          <p class="modal__subtitle">
            Ripristinare l'ordine
            <strong>#{{ currentQuoteId }}</strong> di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong
            >?
          </p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeRestoreModal"
              :disabled="restoring"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmRestore"
              :disabled="restoring"
            >
              <span v-if="restoring">Ripristino…</span>
              <span v-else>Ripristina</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE REIMPOSTA A PREVENTIVO -->
      <VueFinalModal
        v-model="showResetModal"
        :clickToClose="!resetting"
        :escToClose="!resetting"
        classes="documents-modal"
        content-class="confirm-modal-content modal--reset"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeResetModal"
            :disabled="resetting"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Reimposta a preventivo</h3>
          <p class="modal__subtitle">
            Vuoi reimpostare l'Ordine
            <strong>#{{ currentQuoteId }}</strong> da stato
            <strong>'Confermato'</strong> a stato <strong>'In attesa'</strong>?
          </p>
          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeResetModal"
              :disabled="resetting"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmReset"
              :disabled="resetting"
            >
              <span v-if="resetting">Reimposto…</span>
              <span v-else>Reimposta</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE GESTISCI PAGAMENTI -->
      <VueFinalModal
        v-model="showDiscountModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content payments-modal-content"
      >
        <template #default>
          <button class="modal__close icons-button" @click="closePaymentModal">
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">
            Gestisci pagamenti – Ordine #{{ currentQuoteId }}
          </h3>

          <div v-if="paymentLoading" class="history-loading">
            <i class="fa-solid fa-spinner fa-spin"></i> Caricamento riepilogo
            pagamenti…
          </div>

          <div v-else class="quote-modifier">
            <div class="wizard-modifier">
              <div class="wiz-panel">
                <!-- Storico pagamenti -->
                <div class="history-section">
                  <div class="history-section__title">Storico pagamenti</div>
                  <div v-if="paymentHistory.length === 0" class="no-result">
                    Nessun pagamento registrato.
                  </div>
                  <table v-else class="history-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Metodo</th>
                        <th>Importo</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="p in paymentHistory" :key="p.id">
                        <td>
                          {{ formatToLocalDate(p.paidAt ?? p.createdAt) }}
                        </td>
                        <td>
                          {{
                            (
                              getPaymentTypes().find(
                                (t) => t.value === p.type
                              ) || {}
                            ).label || p.type
                          }}
                        </td>
                        <td>{{ p.method }}</td>
                        <td>
                          {{ formatPriceToPrint(Number(p.amount) || 0) }}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colspan="3"><strong>Totale pagato</strong></td>
                        <td>
                          <strong>{{
                            formatPriceToPrint(
                              paymentSummary?.totalePagato || 0
                            )
                          }}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <!-- Tipo pagamento -->
                <div class="payment-type-cards">
                  <button
                    v-for="t in getPaymentTypes()"
                    :key="t.value"
                    type="button"
                    class="payment-type-card"
                    :class="{
                      'payment-type-card--active': paymentType === t.value,
                    }"
                    @click="onPaymentTypeChange(t.value)"
                  >
                    {{ t.label }}
                  </button>
                </div>

                <!-- Importo -->
                <div class="wiz-optional">
                  <p class="wiz-optional-label">
                    <i class="fa-solid fa-circle-info"></i>
                    Importo pagamento
                  </p>
                  <div class="wiz-input-wrap">
                    <span class="wiz-input-prefix">€</span>
                    <input
                      type="number"
                      v-model.number="paymentAmount"
                      min="0"
                      step="1"
                      placeholder="0"
                      class="wiz-input"
                    />
                  </div>
                </div>

                <!-- Metodo di pagamento -->
                <div class="wiz-optional">
                  <p class="wiz-optional-label">
                    <i class="fa-solid fa-credit-card"></i>
                    Metodo di pagamento
                  </p>
                  <div class="wiz-input-wrap wiz-input-wrap--select">
                    <select v-model="paymentMethod" class="wiz-input">
                      <option
                        v-for="m in getPaymentMethods()"
                        :key="m"
                        :value="m"
                      >
                        {{ m }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Riepilogo -->
                <div class="wiz-preview preview breakdown">
                  <ul class="calc-list">
                    <li>
                      Totale ordine:
                      <strong>{{
                        formatPriceToPrint(
                          paymentSummary ? paymentSummary.totaleDovuto : 0
                        )
                      }}</strong>
                    </li>
                    <li>
                      Totale già pagato:
                      <strong>{{
                        formatPriceToPrint(
                          paymentSummary ? paymentSummary.totalePagato : 0
                        )
                      }}</strong>
                    </li>
                    <li>
                      Tipo pagamento:
                      <strong>{{
                        (
                          getPaymentTypes().find(
                            (t) => t.value === paymentType
                          ) || {}
                        ).label
                      }}</strong>
                    </li>
                    <li v-if="(Number(paymentAmount) || 0) > 0">
                      Importo pagamento:
                      <strong
                        >-
                        {{
                          formatPriceToPrint(Number(paymentAmount) || 0)
                        }}</strong
                      >
                    </li>
                    <li class="row-highlight">
                      Rimanente dopo pagamento:
                      <span class="calc-final">
                        <strong>{{
                          formatPriceToPrint(getPaymentRemainingAfterInput())
                        }}</strong>
                      </span>
                    </li>
                  </ul>
                </div>

                <!-- Azioni -->
                <div class="wiz-actions">
                  <button
                    class="wiz-btn"
                    type="button"
                    @click="closePaymentModal"
                  >
                    Annulla
                  </button>
                  <button
                    class="wiz-btn wiz-btn--cta"
                    type="button"
                    :disabled="
                      isPaymentInvalid() ||
                      paymentBusy ||
                      paymentPdfBusy ||
                      paymentLoading
                    "
                    @click="savePayment(selectedQuote)"
                  >
                    <span v-if="paymentBusy || paymentPdfBusy"
                      >Salvataggio e generazione…</span
                    >
                    <span v-else
                      >Salva e genera PDF <i class="fa-solid fa-check"></i
                    ></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE STORICO CONSEGNE -->
      <VueFinalModal
        v-model="showHistoryModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content history-modal-content"
      >
        <template #default>
          <button class="modal__close icons-button" @click="closeHistoryModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
          <h3 class="modal__title">Storico consegne</h3>
          <p class="modal__subtitle">
            Ordine #{{ historyQuote?.id
            }}<template v-if="historyQuote?.Client?.clientName">
              - {{ historyQuote.Client.clientName }}</template
            >
          </p>

          <div v-if="historyLoading" class="history-loading">
            <i class="fa-solid fa-spinner fa-spin"></i> Caricamento…
          </div>

          <template v-else>
            <!-- Consegne DDT -->
            <div class="history-section">
              <div class="history-section__title">Consegne effettuate</div>
              <div v-if="historyDdts.length === 0" class="no-result">
                Nessuna consegna registrata.
              </div>
              <table v-else class="history-table">
                <thead>
                  <tr>
                    <th>N°</th>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Documento</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ddt in historyDdts" :key="ddt.id">
                    <td>{{ ddt.id }}</td>
                    <td>{{ formatToLocalDate(ddt.date ?? ddt.createdAt) }}</td>
                    <td>
                      <span
                        :class="[
                          'history-badge',
                          'history-badge--' + ddt.causale,
                        ]"
                      >
                        {{
                          {
                            consegna: "Consegna",
                            ritiro: "Ritiro",
                          }[ddt.causale] ?? ddt.causale
                        }}
                        <template
                          v-if="ddt.causale === 'consegna' && ddt.sequence"
                        >
                          {{ ddt.sequence }}</template
                        >
                      </span>
                    </td>
                    <td>
                      <a
                        v-if="ddt.documentUrl"
                        :href="ddt.documentUrl"
                        target="_blank"
                        class="custom-link"
                      >
                        <i class="fa-solid fa-file-arrow-down"></i> DDT_N_{{
                          ddt.id
                        }}.pdf
                      </a>
                      <span v-else class="muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Stato articoli -->
            <div class="history-section">
              <div class="history-section__title">Stato articoli</div>
              <div v-if="historyItems.length === 0" class="no-result">
                Nessun articolo.
              </div>
              <div v-else class="history-items-list">
                <div
                  v-for="item in historyItems"
                  :key="item.id ?? item.name"
                  class="history-item-row"
                >
                  <div class="history-item-header">
                    <span class="history-item-name">{{ item.name }}</span>
                    <span class="history-item-qty">
                      {{ item.delivered }} / {{ item.ordered }}
                    </span>
                    <i
                      v-if="item.remaining <= 0"
                      class="fa-solid fa-check history-check"
                    ></i>
                  </div>
                  <div class="history-progress-track">
                    <div
                      class="history-progress-fill"
                      :class="{ complete: item.remaining <= 0 }"
                      :style="{ width: item.pct + '%' }"
                    ></div>
                  </div>
                  <div class="history-item-pct">{{ item.pct }}%</div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </VueFinalModal>

      <!-- DDT MODAL -->
      <DdtModal
        :show="showDdtModal"
        @update:show="showDdtModal = $event"
        :quoteId="ddtModalQuoteId"
        :clientId="ddtModalClientId"
        :clientName="ddtModalClientName"
        :items="ddtModalItems"
        :is-partial-delivery="ddtModalIsPartialDelivery"
        :ddt-consegna-count="ddtModalDdtCount"
        :consegna-complete="ddtModalConsegnaComplete"
        @generated="onDdtGenerated"
      />
    </div>
  </main>
  <router-view></router-view>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import { generateQuotePdf } from "@/utilities/generateQuotePdf";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import {
  formatPriceToPrint,
  formatToLocalDate,
  formatToLocalDateAndHours,
} from "../utilities/formatting";
import DdtModal from "@/components/DdtModal.vue";
import StatusSelect from "@/components/StatusSelect.vue";

const PAGAMENTO_OPTIONS = [
  { value: "In attesa", label: "In attesa", color: "#ffa726" },
  {
    value: "Pagato Parzialmente",
    label: "Pagato Parzialmente",
    color: "#42a5f5",
  },
  { value: "Pagato", label: "Pagato", color: "#4caf50" },
];

const SPEDIZIONE_COLOR = {
  "In magazzino": "#9ca3af",
  "Consegna Parziale": "#fb8c00",
  "In consegna": "#ffa726",
  Consegnato: "#4caf50",
};

export default {
  name: "OrdiniPage",
  components: { DdtModal, StatusSelect },

  computed: {
    ...mapGetters({
      allQuotes: "quotes/list",
      pagination: "quotes/pagination",
    }),

    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },

    filteredDocuments() {
      const list = Array.isArray(this.selectedDocuments)
        ? this.selectedDocuments
        : [];
      const seen = new Set();
      return list.filter(Boolean).filter((doc) => {
        const k = doc.id ?? doc.name ?? doc.url ?? Math.random();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    },

    historyItems() {
      const q = this.historyQuote;
      if (!q) return [];
      const AREA_CATS = [
        "piastrelle",
        "rivestimenti parete",
        "parquet",
        "laminati",
        "decking esterno",
      ];
      const LINEAR_CATS = ["profili", "sistemi di scarico"];
      const rows = [];
      const seen = new Set();
      (q.items || []).forEach((it) => {
        seen.add(it.id);
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        const ordered = Number(it?.QuoteItem?.quantity ?? 0);
        const delivered = Number(
          this.historyDeliveredQtyMap[String(it.id)] || 0
        );
        const pct =
          ordered > 0
            ? Math.min(100, Math.round((delivered / ordered) * 100))
            : 0;
        rows.push({
          id: it.id,
          name: it.itemName || it.supplierCode || it.itemCode || "—",
          ordered,
          delivered,
          remaining: Math.max(0, ordered - delivered),
          uom,
          pct,
        });
      });
      (q.quote_items || []).forEach((qi) => {
        const it = qi?.item || {};
        if (it.id && seen.has(it.id)) return;
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        const ordered = Number(qi.quantity ?? 0);
        const delivered = Number(
          this.historyDeliveredQtyMap[String(it.id)] || 0
        );
        const pct =
          ordered > 0
            ? Math.min(100, Math.round((delivered / ordered) * 100))
            : 0;
        rows.push({
          id: it.id,
          name: it.itemName || it.supplierCode || it.itemCode || "—",
          ordered,
          delivered,
          remaining: Math.max(0, ordered - delivered),
          uom,
          pct,
        });
      });
      return rows;
    },

    ddtDocumentsByCausale() {
      const groups = { consegna: [], ritiro: [] };
      for (const doc of this.selectedDdtDocuments) {
        const key = doc._causale in groups ? doc._causale : "consegna";
        groups[key].push(doc);
      }
      const byDate = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);
      return {
        consegna: [...groups.consegna].sort(byDate),
        ritiro: [...groups.ritiro].sort(byDate),
      };
    },
  },

  data() {
    return {
      isLoading: false,
      PAGAMENTO_OPTIONS,
      refreshing: false,
      refreshed: false,
      searchTerm: "",
      showArchived: false,
      currentQuoteId: null,
      selectedQuote: null,

      showDocumentsModal: false,
      docModalSection: "pdf",
      selectedDocuments: [],
      selectedDdtDocuments: [],
      emailSentMap: {},
      regenPdfBusy: false,

      showConfirmModal: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmLabel: "Conferma",
      confirmBusyLabel: "Aggiorno…",
      confirmBusy: false,
      pendingChange: null,

      showArchiveModal: false,
      archiving: false,
      showRestoreModal: false,
      restoring: false,
      showResetModal: false,
      resetting: false,

      showDdtModal: false,
      ddtModalItems: [],
      ddtModalQuoteId: null,
      ddtModalClientId: null,
      ddtModalClientName: null,
      ddtModalIsPartialDelivery: false,
      ddtModalDdtCount: 0,
      ddtModalConsegnaComplete: false,

      tooltipOpenId: null,
      hoveredId: null,
      hideTimer: null,
      lockClickId: null,

      showDiscountModal: false,
      depositAmount: 0,
      removeModifierBusy: false,

      // Gestione pagamenti (quote_payments)
      paymentType: "acconto",
      paymentAmount: 0,
      paymentMethod: "Contanti",
      paymentSummary: null,
      paymentLoading: false,
      paymentBusy: false,
      paymentPdfBusy: false,
      paymentHistory: [],

      showHistoryModal: false,
      historyQuote: null,
      historyDdts: [],
      historyDeliveredQtyMap: {},
      historyLoading: false,
    };
  },

  methods: {
    ...mapActions([
      "fetchQuotes",
      "archiveQuote",
      "restoreQuote",
      "sendQuoteDocumentEmail",
    ]),

    formatPriceToPrint,
    formatToLocalDate,
    formatToLocalDateAndHours,

    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.getQuotes(this.pagination.currentPage);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    async getQuotes(page = 1, search = this.searchTerm) {
      this.isLoading = true;
      try {
        await this.fetchQuotes({
          page,
          limit: 7,
          search,
          archived: this.showArchived,
          isConfirmed: true,
        });
      } catch (err) {
        if (err?.response?.status === 404 && page !== 1) {
          await this.getQuotes(1, search);
          return;
        }
        if (err?.response?.status !== 404) {
          this.$toast.error(
            err?.response?.data?.message ||
              "Errore nel caricamento degli ordini."
          );
        }
      } finally {
        this.isLoading = false;
      }
    },

    async switchTab(archived) {
      this.showArchived = archived;
      this.searchTerm = "";
      await this.getQuotes(1);
    },

    async onSearch() {
      await this.getQuotes(1);
    },

    async onResetSearch() {
      this.searchTerm = "";
      await this.getQuotes(1);
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.getQuotes(page);
    },

    spedizioneOptions(quote) {
      const s = quote.state;
      let opts;
      if (s === "In magazzino") {
        opts = [
          { value: "In magazzino", label: "In magazzino", disabled: true },
          {
            value: "Consegna Parziale",
            label: "Consegna Parziale",
            disabled: false,
          },
          { value: "In consegna", label: "In consegna", disabled: false },
        ];
      } else if (s === "Consegna Parziale") {
        opts = [
          {
            value: "Consegna Parziale",
            label: "Consegna Parziale",
            disabled: true,
          },
          { value: "Consegnato", label: "Consegnato", disabled: false },
        ];
      } else if (s === "In consegna") {
        opts = [
          { value: "In consegna", label: "In consegna", disabled: true },
          { value: "Consegnato", label: "Consegnato", disabled: false },
        ];
      } else {
        opts = [
          {
            value: s || "Consegnato",
            label: s || "Consegnato",
            disabled: true,
          },
        ];
      }
      return opts.map((o) => ({
        ...o,
        color: SPEDIZIONE_COLOR[o.value] ?? "#9ca3af",
      }));
    },

    isStateSelectDisabled(quote) {
      if (quote.state === "Consegnato") return true;
      if (quote.state === "In magazzino")
        return !["Pagato", "Pagato Parzialmente"].includes(quote.payment_state);
      if (quote.state === "Consegna Parziale") return false;
      return false;
    },

    formatAddress(address) {
      if (!address) return "—";
      const withCap = address.replace(/,?\s+(\d{5}\s)/, "\n$1");
      if (withCap !== address) return withCap;
      return address.replace(/,\s*/, ",\n");
    },

    onQuoteStateChange(quote, field, newValue) {
      this.pendingChange = { id: quote.id, field, newValue };
      const qid = quote?.id ?? "—";
      const clientName = quote?.Client?.clientName ?? "cliente";

      if (field === "payment_state" && newValue === "Pagato Parzialmente") {
        this.confirmTitle = "Conferma pagamento parziale";
        this.confirmMessage =
          `Impostare il pagamento su <strong>Pagato Parzialmente</strong> per l'ordine ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?`;
        this.confirmLabel = "Stato => Pagato Parzialmente";
        this.confirmBusyLabel = "Imposto…";
      } else if (field === "payment_state" && newValue === "Pagato") {
        const warn =
          quote.state === "Consegnato"
            ? " Confermando <strong>Pagato</strong> non potrai più modificarlo."
            : "";
        this.confirmTitle = "Conferma pagamento";
        this.confirmMessage =
          `Impostare il pagamento su <strong>Pagato</strong> per l'ordine ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Pagato";
        this.confirmBusyLabel = "Imposto…";
      } else if (field === "state" && newValue === "Consegna Parziale") {
        this.confirmTitle = "Avvia consegna parziale";
        this.confirmMessage =
          `Avviare la consegna parziale per l'ordine <strong>#${qid}</strong> di ` +
          `<strong>${clientName}</strong>?<br>Potrai generare più DDT di consegna in momenti diversi.`;
        this.confirmLabel = "Avvia consegna parziale";
        this.confirmBusyLabel = "Aggiorno…";
      } else if (field === "state" && newValue === "In consegna") {
        this.confirmTitle = "Manda in consegna";
        this.confirmMessage =
          `Mandare in consegna l'ordine <strong>#${qid}</strong> di ` +
          `<strong>${clientName}</strong>?<br>Questa azione scaricherà lo stock degli articoli.`;
        this.confirmLabel = "Manda in consegna";
        this.confirmBusyLabel = "Aggiorno…";
      } else if (field === "state" && newValue === "Consegnato") {
        const warn =
          quote.payment_state === "Pagato"
            ? " Confermando <strong>Consegnato</strong> non potrai più modificarlo."
            : "";
        this.confirmTitle = "Conferma consegna";
        this.confirmMessage =
          `Segnare come <strong>Consegnato</strong> l'ordine ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Consegnato";
        this.confirmBusyLabel = "Aggiorno…";
      } else {
        this.confirmTitle = "Conferma modifica";
        this.confirmMessage = `Confermare la modifica per l'ordine <strong>#${qid}</strong>?`;
        this.confirmLabel = "Conferma";
        this.confirmBusyLabel = "Aggiorno…";
      }
      this.showConfirmModal = true;
    },

    handleConfirmCancel() {
      this.showConfirmModal = false;
      this.pendingChange = null;
      this.getQuotes(this.pagination.currentPage);
    },

    async handleConfirmOK() {
      if (!this.pendingChange) return;
      const { id, field, newValue } = this.pendingChange;
      this.confirmBusy = true;
      try {
        await this.updateQuoteState(id, newValue);
      } finally {
        this.confirmBusy = false;
        this.showConfirmModal = false;
        this.pendingChange = null;
      }
      // Cambio manuale dello stato spedizione (senza generare DDT): apri la
      // modale DDT per generare il documento di trasporto corrispondente.
      if (field === "state") {
        const q = this.allQuotes.find((x) => x.id === id);
        // apri solo se l'aggiornamento è andato a buon fine (stato allineato)
        if (q && q.state === newValue) {
          this.openDdtModal(q);
        }
      }
    },

    async updateQuoteState(quoteId, newState) {
      try {
        const payload = JSON.stringify({ state: newState });
        const response = await this.$axios.patch(
          `quote/state/${quoteId}`,
          payload
        );

        if (response.status === 200 || response.status === 201) {
          const successMap = {
            "Pagato Parzialmente": "Stato aggiornato a 'Pagato Parzialmente'.",
            Pagato: "Stato aggiornato a 'Pagato'.",
            Consegnato: "Stato aggiornato a 'Consegnato'.",
            "In consegna": "Stato aggiornato a 'In consegna'.",
            "Consegna Parziale": "Consegna parziale avviata.",
          };
          this.$toast.success(
            response.data?.message ||
              successMap[newState] ||
              "Stato aggiornato."
          );

          const q = this.allQuotes.find((q) => q.id === quoteId);
          if (q) {
            if (newState === "Pagato" || newState === "Pagato Parzialmente")
              q.payment_state = newState;
            else q.state = newState;
            if (newState === "Consegna Parziale")
              q.ddtConsegnaCount = q.ddtConsegnaCount ?? 0;
          }

          if (q?.payment_state === "Pagato" && q?.state === "Consegnato") {
            setTimeout(async () => {
              await this.getQuotes(this.pagination.currentPage);
              this.$toast.success("Ordine completato e archiviato.");
            }, 1000);
          }
        }
      } catch (err) {
        const data = err.response?.data ?? {};
        const msg =
          data.message ||
          data.details ||
          data.error ||
          "Errore durante l'aggiornamento.";
        this.$toast.error(msg);
        this.getQuotes(this.pagination.currentPage);
      }
    },

    /* ---- Documenti ---- */
    async openDocumentsModal(quote, section = "pdf") {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.docModalSection = section;
      this.selectedDocuments = [];
      this.selectedDdtDocuments = [];
      this.showDocumentsModal = true;

      const quoteDocs = (quote.documents || []).filter(Boolean).map((d) => ({
        ...d,
        _quoteUpdatedAt: quote.updatedAt ?? null,
      }));

      let ddtDocs = [];
      if (quote.id) {
        try {
          const { data } = await axiosInstance.get("/ddt", {
            params: { quoteId: quote.id },
          });
          ddtDocs = (data || [])
            .filter((d) => d.documentUrl)
            .map((d) => ({
              id: `ddt-${d.id}`,
              name: `DDT_N_${d.id}.pdf`,
              url: d.documentUrl,
              createdAt: d.date ?? d.createdAt,
              _causale: d.causale,
              _ddtId: d.id,
            }));
        } catch {
          /* ignora errori fetch DDT */
        }
      }
      this.selectedDocuments = quoteDocs;
      this.selectedDdtDocuments = ddtDocs;
    },

    closeDocumentsModal() {
      this.showDocumentsModal = false;
      this.selectedDocuments = [];
      this.selectedDdtDocuments = [];
      this.currentQuoteId = null;
      this.selectedQuote = null;
    },

    // Rigenera il PDF ordine aggiornato (items/sconto/pagamenti correnti):
    // lo scarica sul PC e lo aggiorna tra i documenti dell'ordine.
    async regenerateOrderPdf() {
      const quote = this.selectedQuote;
      if (!quote?.id || this.regenPdfBusy) return;
      this.regenPdfBusy = true;
      try {
        // Dati completi ordine: preferisci il locale se ha già le righe con item,
        // altrimenti ri-fetcha il dettaglio.
        const q = this.allQuotes.find((x) => x.id === quote.id) || quote;
        const needsFetch = !q?.quote_items?.some((qi) => qi?.item);
        let dataForPdf;
        if (needsFetch) {
          const res = await axiosInstance.get("/quote/" + quote.id);
          dataForPdf = res.data?.quote || res.data?.data || res.data;
        } else {
          dataForPdf = { ...q };
        }
        if (!dataForPdf.id) dataForPdf.id = quote.id;
        // Storico pagamenti per il riepilogo (acconti + saldi)
        try {
          const { data } = await axiosInstance.get(
            "quote/" + quote.id + "/payments"
          );
          dataForPdf.payments = data?.payments || dataForPdf.payments || [];
        } catch {
          dataForPdf.payments = dataForPdf.payments || [];
        }
        await generateQuotePdf({
          quote: dataForPdf,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          isOrder: true,
          onComplete: () => this.refreshOrderDocuments(quote.id),
        });
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            "Errore nella rigenerazione del PDF."
        );
      } finally {
        this.regenPdfBusy = false;
      }
    },

    // Ricarica i documenti dell'ordine dopo la rigenerazione, così la card
    // aggiornata compare subito nella modale (e nell'elenco).
    async refreshOrderDocuments(id) {
      try {
        const res = await axiosInstance.get("/quote/" + id);
        const fresh = res.data?.quote || res.data?.data || res.data;
        const docs = (fresh?.documents || []).filter(Boolean).map((d) => ({
          ...d,
          _quoteUpdatedAt: fresh?.updatedAt ?? null,
        }));
        this.selectedDocuments = docs;
        const q = this.allQuotes.find((x) => x.id === id);
        if (q && fresh?.documents) q.documents = fresh.documents;
        if (this.selectedQuote && fresh?.documents)
          this.selectedQuote.documents = fresh.documents;
      } catch {
        /* refresh best-effort: se fallisce, i documenti si aggiornano riaprendo */
      }
    },

    async onSendQuoteEmail(quote) {
      const doc = (quote?.documents || []).at(-1);
      if (!doc) return;
      try {
        await this.sendQuoteDocumentEmail({
          quoteId: quote.id,
          objectName: doc.name,
        });
        this.emailSentMap = { ...this.emailSentMap, [quote.id]: true };
        this.$toast.success("Email inviata al cliente con successo.");
      } catch (err) {
        this.$toast.error(
          err.response?.data?.error || "Errore durante l'invio dell'email."
        );
      }
    },

    /* ---- DDT ---- */
    openDiscountModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.depositAmount = 0;
      this.showDiscountModal = true;
    },
    closeDiscountModal() {
      this.showDiscountModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
      this.depositAmount = 0;
    },

    /* ---- Gestione pagamenti (quote_payments) ---- */
    getPaymentTypes() {
      return [
        { value: "acconto", label: "Acconto" },
        { value: "saldo_parziale", label: "Saldo parziale" },
        { value: "saldo_finale", label: "Saldo finale" },
      ];
    },
    getPaymentMethods() {
      return ["Contanti", "Bonifico", "Carta di credito"];
    },
    openPaymentModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      // reset stato nuovo
      this.paymentType = "acconto";
      this.paymentAmount = 0;
      this.paymentMethod = "Contanti";
      this.paymentSummary = null;
      this.paymentHistory = [];
      // per ora riusiamo ancora la vecchia modale, il template lo cambiamo dopo
      this.showDiscountModal = true;
      if (quote?.id != null) this.loadPaymentSummary(quote.id);
    },
    closePaymentModal() {
      this.showDiscountModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
      this.paymentType = "acconto";
      this.paymentAmount = 0;
      this.paymentMethod = "Contanti";
      this.paymentSummary = null;
      this.paymentHistory = [];
    },
    async loadPaymentSummary(id) {
      this.paymentLoading = true;
      try {
        const { data } = await axiosInstance.get("quote/" + id + "/payments");
        this.paymentSummary = {
          totaleDovuto: Number(data?.totaleDovuto) || 0,
          totalePagato: Number(data?.totalePagato) || 0,
          rimanente: Number(data?.rimanente) || 0,
        };
        this.paymentHistory = data?.payments || [];
      } catch (e) {
        this.paymentSummary = null;
        this.paymentHistory = [];
        this.$toast.error(
          e?.response?.data?.message ||
            "Errore nel caricamento del riepilogo pagamenti."
        );
      } finally {
        this.paymentLoading = false;
      }
    },
    onPaymentTypeChange(type) {
      this.paymentType = type;
      if (type === "saldo_finale" && this.paymentSummary) {
        this.paymentAmount = this.paymentSummary.rimanente;
      }
    },
    getPaymentRemainingAfterInput() {
      return Math.max(
        0,
        (this.paymentSummary?.rimanente || 0) -
          (Number(this.paymentAmount) || 0)
      );
    },
    isPaymentInvalid() {
      const amount = Number(this.paymentAmount);
      return (
        this.paymentLoading ||
        this.paymentBusy ||
        this.paymentSummary === null ||
        !Number.isFinite(amount) ||
        amount <= 0 ||
        amount > this.paymentSummary.rimanente
      );
    },
    async savePayment(quote) {
      if (!quote?.id || this.isPaymentInvalid()) return;
      this.paymentBusy = true;
      let saved = false;
      try {
        const { data } = await axiosInstance.post(
          "quote/" + quote.id + "/payments",
          {
            type: this.paymentType,
            amount: Number(this.paymentAmount),
            method: this.paymentMethod,
          }
        );

        // aggiorna localmente la quote in elenco
        const q = this.allQuotes.find((x) => x.id === quote.id);
        if (q) {
          q.deposit = data.deposit;
          q.payment_state = data.payment_state;
        }
        // aggiorna anche l'oggetto quote passato, se diverso
        if (quote && quote !== q) {
          quote.deposit = data.deposit;
          quote.payment_state = data.payment_state;
        }

        // aggiorna riepilogo
        this.paymentSummary = {
          totaleDovuto: Number(data.totaleDovuto) || 0,
          totalePagato: Number(data.totalePagato) || 0,
          rimanente: Number(data.rimanente) || 0,
        };
        if (data.payment) this.paymentHistory.push(data.payment);

        this.$toast.success(data.message || "Pagamento registrato.");
        saved = true;
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            e?.response?.data?.error ||
            "Errore nel salvataggio del pagamento."
        );
      } finally {
        this.paymentBusy = false;
      }

      // se il pagamento non è stato salvato: niente PDF, modale aperta
      if (!saved) return;

      // pagamento salvato: genera il PDF ordine (nessun rollback se fallisce)
      this.paymentPdfBusy = true;
      try {
        // Stessa logica del vecchio applyDeposit: il GET /quote/:id è un wrapper
        // e non contiene tutti i campi del PDF, quindi si preferisce il quote
        // locale (già aggiornato con deposit/payment_state dal POST).
        const q = this.allQuotes.find((x) => x.id === quote.id);
        const needsFetch = !q?.quote_items?.some((qi) => qi?.item);
        let dataForPdf;
        if (needsFetch) {
          const res = await axiosInstance.get("/quote/" + quote.id);
          dataForPdf = res.data?.quote || res.data?.data || res.data;
        } else {
          dataForPdf = { ...q };
        }
        // id sempre valido (necessario per filename e upload)
        if (!dataForPdf.id) dataForPdf.id = quote.id;
        // payments non incluso da lista/dettaglio: usa lo storico già caricato
        if (!dataForPdf.payments)
          dataForPdf.payments = this.paymentHistory || [];
        if (!dataForPdf.id) {
          throw new Error("Impossibile generare il PDF: id ordine mancante.");
        }
        // Nome descrittivo/univoco dal pagamento appena registrato: il PDF
        // entra nello storico come documento distinto e non sovrascrive
        // "Ordine_N_<id>.pdf".
        const filenameOverride = this.buildPaymentFilename(
          dataForPdf.payments || this.paymentHistory
        );
        await generateQuotePdf({
          quote: dataForPdf,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          isOrder: true,
          filenameOverride,
        });
        // chiudi la modale solo dopo la generazione PDF riuscita
        this.closePaymentModal();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            e?.response?.data?.error ||
            "Pagamento salvato, ma errore nella generazione del PDF."
        );
      } finally {
        this.paymentPdfBusy = false;
      }
    },

    async applyDeposit(quote) {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      const amount = round2(Number(this.depositAmount));
      if (!amount) return;
      this.confirmBusy = true;
      try {
        await axiosInstance.post(`quote/priceAdjustment/${quote.id}`, {
          type: "deposit",
          amount,
        });
        const q = this.allQuotes.find((x) => x.id === quote.id);
        if (q) q.deposit = round2((Number(q.deposit) || 0) + amount);
        this.$toast.success("Acconto registrato.");
        const needsFetch = !q?.quote_items?.some((qi) => qi?.item);
        const dataForPdf = needsFetch
          ? await axiosInstance.get(`/quote/${quote.id}`).then((r) => r.data)
          : q;
        // Flusso legacy "acconto": pagamento sintetico per ottenere un nome
        // descrittivo/univoco senza sovrascrivere "Ordine_N_<id>.pdf".
        const filenameOverride = this.buildPaymentFilename([
          { type: "acconto", paidAt: new Date().toISOString() },
        ]);
        await generateQuotePdf({
          quote: dataForPdf,
          toast: this.$toast,
          uploadQuoteDocument: (payload) =>
            this.$store.dispatch("uploadQuoteDocument", payload),
          isOrder: true,
          filenameOverride,
        });
        this.closeDiscountModal();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            "Errore nel salvataggio."
        );
      } finally {
        this.confirmBusy = false;
      }
    },
    async removeModifier(quote, type) {
      this.removeModifierBusy = true;
      try {
        await axiosInstance.delete(`quote/priceAdjustment/${quote.id}`, {
          data: { type },
        });
        const q = this.allQuotes.find((x) => x.id === quote.id);
        if (q && type === "deposit") q.deposit = 0;
        this.$toast.success("Acconto rimosso.");
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nella rimozione."
        );
      } finally {
        this.removeModifierBusy = false;
      }
    },

    async openHistoryModal(quote) {
      this.historyQuote = quote;
      this.historyDdts = [];
      this.historyDeliveredQtyMap = {};
      this.historyLoading = true;
      this.showHistoryModal = true;
      try {
        const [ddtRes, deliveredRes] = await Promise.allSettled([
          axiosInstance.get("/ddt", { params: { quoteId: quote.id } }),
          this.$axios.get(`ddt/quote/${quote.id}/delivered-qty`),
        ]);
        if (ddtRes.status === "fulfilled") {
          this.historyDdts = (ddtRes.value.data || []).sort(
            (a, b) =>
              new Date(a.date ?? a.createdAt) - new Date(b.date ?? b.createdAt)
          );
        }
        if (deliveredRes.status === "fulfilled") {
          this.historyDeliveredQtyMap = Object.fromEntries(
            (deliveredRes.value.data || []).map((r) => [
              String(r.itemId),
              Number(r.totalDelivered),
            ])
          );
        }
      } finally {
        this.historyLoading = false;
      }
    },

    closeHistoryModal() {
      this.showHistoryModal = false;
      this.historyQuote = null;
      this.historyDdts = [];
      this.historyDeliveredQtyMap = {};
    },

    async openDdtModal(quote) {
      const AREA_CATS = [
        "piastrelle",
        "rivestimenti parete",
        "parquet",
        "laminati",
        "decking esterno",
      ];
      const LINEAR_CATS = ["profili", "sistemi di scarico"];
      const rows = [];
      const seen = new Set();

      (quote.items || []).forEach((it) => {
        seen.add(it.id);
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        rows.push({
          itemId: it.id,
          description: it.itemName || it.supplierCode || it.itemCode || "—",
          quantity: it?.QuoteItem?.quantity ?? 0,
          uom,
          deliveredQty: 0,
        });
      });

      (quote.quote_items || []).forEach((qi) => {
        if (qi?.item?.id && seen.has(qi.item.id)) return;
        const it = qi.item || {};
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        rows.push({
          itemId: it.id || null,
          description: it.itemName || it.supplierCode || it.itemCode || "—",
          quantity: qi.quantity ?? 0,
          uom,
          deliveredQty: 0,
        });
      });

      const isPartial = quote.state === "Consegna Parziale";
      const ddtCount = Number(quote.ddtConsegnaCount) || 0;

      if (ddtCount > 0) {
        try {
          const { data: deliveredQtys } = await this.$axios.get(
            `ddt/quote/${quote.id}/delivered-qty`
          );
          const deliveredMap = Object.fromEntries(
            deliveredQtys.map((r) => [
              String(r.itemId),
              Number(r.totalDelivered),
            ])
          );
          const updatedRows = rows.map((row) => {
            const delivered =
              row.itemId != null ? deliveredMap[String(row.itemId)] || 0 : 0;
            return {
              ...row,
              deliveredQty: delivered,
              quantity: Math.max(0, Number(row.quantity) - delivered),
            };
          });
          rows.splice(0, rows.length, ...updatedRows);
        } catch (e) {
          console.error("delivered-qty error", e);
        }
      }

      this.ddtModalItems = rows;
      this.ddtModalQuoteId = quote.id;
      this.ddtModalClientId = quote.clientId || null;
      this.ddtModalClientName = quote?.Client?.clientName || null;
      this.ddtModalIsPartialDelivery = isPartial;
      this.ddtModalDdtCount = ddtCount;
      this.ddtModalConsegnaComplete = false;
      await this.$nextTick();
      this.showDdtModal = true;
    },

    async onDdtGenerated({ ddt, isLastDelivery }) {
      const q = this.allQuotes.find((q) => q.id === ddt.quoteId);
      if (q) q.ddtConsegnaCount = (Number(q.ddtConsegnaCount) || 0) + 1;
      if (!ddt.quoteId) return;
      if (isLastDelivery) {
        // Tutti gli articoli consegnati → forziamo Consegnato (il backend
        // imposta da solo solo "Consegna Parziale", non il completamento).
        try {
          await this.updateQuoteState(ddt.quoteId, "Consegnato");
        } catch {
          this.$toast.error("DDT generato ma errore nell'aggiornamento stato.");
        }
      } else if (q && q.state !== "Consegnato") {
        // Consegna parziale: il backend ha GIÀ impostato "Consegna Parziale"
        // alla creazione del DDT. Rispecchiamo lo stato in locale (reattivo,
        // senza refresh) evitando un PATCH ridondante che darebbe errore
        // "ordine già in consegna parziale".
        q.state = "Consegna Parziale";
      }
    },

    /* ---- Archivio ---- */
    openArchiveModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showArchiveModal = true;
    },
    closeArchiveModal() {
      if (this.archiving) return;
      this.showArchiveModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmArchive() {
      if (!this.selectedQuote) return;
      this.archiving = true;
      try {
        const response = await this.archiveQuote(this.selectedQuote.id);
        this.$toast.success(
          response?.data?.message || "Ordine archiviato con successo."
        );
        this.showArchiveModal = false;
        this.currentQuoteId = null;
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.$toast.error(
          err.response?.data?.details || "Impossibile archiviare l'ordine."
        );
      } finally {
        this.archiving = false;
        this.selectedQuote = null;
      }
    },

    openRestoreModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showRestoreModal = true;
    },
    closeRestoreModal() {
      if (this.restoring) return;
      this.showRestoreModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmRestore() {
      if (!this.selectedQuote) return;
      this.restoring = true;
      try {
        const response = await this.restoreQuote(this.selectedQuote.id);
        this.$toast.success(
          response?.data?.message || "Ordine ripristinato con successo."
        );
        this.showRestoreModal = false;
        this.currentQuoteId = null;
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.$toast.error(
          err.response?.data?.message || "Impossibile ripristinare l'ordine."
        );
      } finally {
        this.restoring = false;
        this.selectedQuote = null;
      }
    },

    openResetModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showResetModal = true;
    },
    closeResetModal() {
      if (this.resetting) return;
      this.showResetModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmReset() {
      if (!this.selectedQuote) return;
      this.resetting = true;
      try {
        await this.updateQuoteState(this.selectedQuote.id, false);
        this.$toast.success("Ordine reimpostato a preventivo.");
        this.showResetModal = false;
        this.currentQuoteId = null;
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.$toast.error(
          err.response?.data?.message ||
            "Impossibile reimpostare l'ordine a preventivo."
        );
      } finally {
        this.resetting = false;
        this.selectedQuote = null;
      }
    },

    isCompleted(quote) {
      return quote?.payment_state === "Pagato" && quote?.state === "Consegnato";
    },
    canRestore(quote) {
      return !this.isCompleted(quote);
    },

    /* ---- Tooltip cliente ---- */
    isTooltipVisible(id) {
      return this.tooltipOpenId === id || this.hoveredId === id;
    },
    applyTooltipPlacement(id) {
      const tip = document.getElementById("tt-" + id);
      if (tip) tip.dataset.placement = "over";
    },
    onCellEnter(id) {
      clearTimeout(this.hideTimer);
      this.hoveredId = id;
      this.$nextTick(() => this.applyTooltipPlacement(id));
    },
    onCellLeave(id) {
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        if (this.tooltipOpenId !== id) this.hoveredId = null;
      }, 120);
    },
    toggleTooltip(id) {
      this.tooltipOpenId = this.tooltipOpenId === id ? null : id;
      if (this.tooltipOpenId === id) {
        this.hoveredId = id;
        this.lockClickId = id;
        setTimeout(() => {
          if (this.lockClickId === id) this.lockClickId = null;
        }, 250);
        this.$nextTick(() => this.applyTooltipPlacement(id));
      }
    },
    onTriggerTouch(id) {
      this.tooltipOpenId = this.tooltipOpenId === id ? null : id;
      if (this.tooltipOpenId === id) {
        this.hoveredId = id;
        this.lockClickId = id;
        setTimeout(() => {
          if (this.lockClickId === id) this.lockClickId = null;
        }, 250);
        this.$nextTick(() => this.applyTooltipPlacement(id));
      }
    },
    onTriggerKeydown(e, id) {
      if (e.key === "Escape") {
        this.tooltipOpenId = null;
        this.hoveredId = null;
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggleTooltip(id);
      }
    },
    handleDocumentClick(e) {
      if (!e.target.closest(".client-cell")) this.tooltipOpenId = null;
    },

    async copyToClipboard(text, kind = null) {
      const value = String(text ?? "").trim();
      if (!kind) {
        if (/^\+?[\d()\s.-]{5,}$/.test(value)) kind = "phone";
        else if (/@/.test(value)) kind = "email";
        else kind = "text";
      }
      try {
        await navigator.clipboard.writeText(value);
        const msgMap = {
          phone: "Numero copiato",
          email: "Email copiata",
          pec: "PEC copiata",
          text: "Testo copiato",
        };
        this.$toast?.success?.(`${msgMap[kind] || msgMap.text} negli appunti`);
      } catch {
        this.$toast?.error?.("Impossibile copiare negli appunti");
      }
    },

    safeDocKey(doc, i) {
      if (doc && doc.id != null) return "doc-" + String(doc.id);
      if (doc && doc.url) return "url-" + doc.url;
      if (doc && doc.name) return "name-" + doc.name;
      return "idx-" + String(i);
    },

    // Costruisce il filename descrittivo e univoco di un PDF pagamento a partire
    // dall'ultimo pagamento della lista: "tipologia_YYYY-MM-DD_HH-mm.pdf".
    // Data preferita: paidAt → createdAt → ora corrente.
    buildPaymentFilename(payments) {
      const list = [...(payments || [])];
      if (!list.length) return null;
      const last = list.sort((a, b) => {
        const da = new Date(a?.paidAt ?? a?.createdAt ?? 0).getTime();
        const db = new Date(b?.paidAt ?? b?.createdAt ?? 0).getTime();
        if (da !== db) return da - db;
        return (Number(a?.id) || 0) - (Number(b?.id) || 0);
      })[list.length - 1];

      const TYPE_SLUG = {
        acconto: "acconto",
        saldo_parziale: "saldo-parziale",
        saldo_finale: "saldo-finale",
      };
      const slug = TYPE_SLUG[last?.type] || "pagamento";

      const d = new Date(last?.paidAt ?? last?.createdAt ?? Date.now());
      const pad = (n) => String(n).padStart(2, "0");
      const stamp =
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `_${pad(d.getHours())}-${pad(d.getMinutes())}`;

      return `${slug}_${stamp}.pdf`;
    },

    // In pagina Ordini il documento ordine principale è presentato come "Ordine"
    // anche se il file salvato si chiama ancora "Preventivo_N_X" (nessun rename
    // retroattivo). I PDF pagamento sono resi in forma leggibile.
    formatDocName(name) {
      const raw = (name ?? "").toString().replace(/\.dev(?=\.pdf$)/i, "");

      const PAY_LABELS = {
        acconto: "Acconto",
        "saldo-parziale": "Saldo parziale",
        "saldo-finale": "Saldo finale",
      };
      const m = raw.match(
        /^(acconto|saldo-parziale|saldo-finale)_(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})\.pdf$/i
      );
      if (m) {
        const [, type, Y, Mo, D, H, Mi] = m;
        return `${PAY_LABELS[type.toLowerCase()]} - ${D}/${Mo}/${Y} ${H}:${Mi}`;
      }

      return raw.replace(/_N_\d+\.pdf$/i, "").replace(/^Preventivo/i, "Ordine");
    },
  },

  watch: {
    $route(to) {
      if (to.name === "ordini") {
        this.getQuotes(1);
      }
    },
    searchTerm() {
      this.onSearch();
    },
  },

  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
    this.getQuotes(1);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
};
</script>

<style src="@/css/OrdiniPage.css" />
